import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Calendar,
  Clock,
  MapPin,
  Sparkles,
  Mic2,
} from 'lucide-react';
import './therme-landing.css';
import { THERME_NOTICES, THERME_PROGRAMME, THERME_CEREMONY_LABEL, THERME_REGISTRATION_LABEL, THERME_VENUE_LABEL } from './therme-groundbreaking-content';
import { ThermeEventLogo, useThermePageTitle } from './therme-landing-shared';
import { ThermeGettingTherePremium } from './ThermeGettingThereSection';

const FONT_LINK_ID = 'therme-gg-google-fonts';
const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,opsz,wght@0,6..96,400;0,6..96,600;0,6..96,700;1,6..96,400&family=Jost:wght@300;400;500;600&display=swap';

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatDateRange(startDate) {
  if (!startDate) return '';
  const d = new Date(`${startDate}T00:00:00`);
  const day = DAYS[d.getDay()];
  const date = d.getDate();
  const month = MONTHS_FULL[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${date} ${month} ${year}, 9:30 a.m.–11:30 a.m.`;
}

export default function ThermeGroundbreakingLanding({ event: e, slug, registerPath }) {
  useThermePageTitle();

  const venueLabel = slug === 'thermesggbreak' ? THERME_VENUE_LABEL : e?.venue;

  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return undefined;
    const link = document.createElement('link');
    link.id = FONT_LINK_ID;
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
    return () => {
      const el = document.getElementById(FONT_LINK_ID);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="therme-gg-root min-h-screen">
      <header className="therme-nav fixed z-50 mx-auto max-w-6xl">
        <div className="flex h-14 items-center justify-between px-5 md:px-6">
          <ThermeEventLogo event={e} className="text-[var(--therme-text)]" />
          <Link to={registerPath} className="therme-btn-primary" data-testid="public-register-cta">
            Register <ArrowRight size={14} aria-hidden />
          </Link>
        </div>
      </header>

      <section className="relative min-h-[88vh] overflow-hidden pt-28 pb-16 md:pt-36 md:pb-24">
        <div className="absolute inset-0">
          {e?.cover ? (
            <img src={e.cover} alt="" className="h-full w-full object-cover opacity-35" />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628]/40 via-[#0a1628]/85 to-[#0a1628]" />
          <div className="therme-hero-glow absolute inset-0 pointer-events-none" />
        </div>

        <div className="relative mx-auto max-w-6xl px-4 md:px-8">
          <p className="mb-4 flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.22em] text-[var(--therme-muted)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--therme-gold)]" aria-hidden />
            {(e?.startDate || 'TBD').replaceAll('-', '/')} — {(e?.endDate || 'TBD').replaceAll('-', '/')}
          </p>
          <h1 className="therme-display text-[clamp(2.25rem,8vw,5.5rem)] font-semibold leading-[1.02] tracking-tight">
            {e?.title}
            {e?.startDate?.slice(0, 4) ? (
              <span className="mt-2 block text-[var(--therme-gold)]">/ {e.startDate.slice(0, 4)}</span>
            ) : null}
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[var(--therme-muted)] md:text-lg">
            A landmark moment for Therme in Singapore — join us for the official groundbreaking ceremony.
          </p>
          <div className="mt-10">
            <Link to={registerPath} className="therme-btn-primary" data-testid="hero-register">
              Save your seat <ArrowRight size={14} aria-hidden />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {[
              [Calendar, 'Date', formatDateRange(e?.startDate)],
              [MapPin, 'Venue', venueLabel],
              [Clock, 'Registration', slug === 'thermesggbreak' ? THERME_REGISTRATION_LABEL : 'Opens on event day'],
              [Sparkles, 'Ceremony', slug === 'thermesggbreak' ? THERME_CEREMONY_LABEL : 'In-person'],
            ].map(([Icon, label, value]) => (
              <div key={label} className="therme-glass-card p-4 md:p-5">
                <Icon size={16} className="text-[var(--therme-gold)]" aria-hidden />
                <div className="mt-3 text-[10px] font-medium uppercase tracking-[0.18em] text-[var(--therme-muted)]">
                  {label}
                </div>
                <div
                  className={`mt-1 text-sm leading-relaxed text-[var(--therme-text)] ${
                    label === 'Venue' ? 'whitespace-pre-line' : ''
                  }`}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="programme" className="relative border-t border-white/10 py-20 md:py-28">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <div className="mb-12 md:mb-16 md:flex md:items-end md:justify-between md:gap-8">
            <div>
              <div className="therme-gold-line mb-5" aria-hidden />
              <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[var(--therme-gold)]">
                On the day
              </p>
              <h2 className="therme-display mt-3 text-3xl font-semibold tracking-tight md:text-5xl">
                Programme highlights
              </h2>
            </div>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-[var(--therme-muted)] md:mt-0 md:text-base">
              A curated sequence of addresses and the ceremonial groundbreaking, followed by partnership announcements.
            </p>
          </div>

          <ol className="space-y-0">
            {THERME_PROGRAMME.map((item, index) => (
              <li key={item.title} className="flex gap-5 md:gap-8">
                <div className="flex flex-col items-center pt-1">
                  <span className="therme-timeline-dot" aria-hidden />
                  {index < THERME_PROGRAMME.length - 1 ? (
                    <div className="therme-timeline-line mt-2 min-h-[4rem] flex-1 md:min-h-[5rem]" aria-hidden />
                  ) : null}
                </div>
                <article className="therme-glass-card mb-4 flex-1 cursor-default p-6 md:p-8">
                  <div className="flex items-start gap-3">
                    <Mic2 size={18} className="mt-0.5 shrink-0 text-[var(--therme-gold)]" aria-hidden />
                    <div>
                      <h3 className="therme-display text-xl font-semibold md:text-2xl">{item.title}</h3>
                      {item.speaker ? (
                        <p className="mt-2 text-base font-medium text-[var(--therme-text)]">{item.speaker}</p>
                      ) : null}
                      <p className="mt-1 text-sm leading-relaxed text-[var(--therme-muted)] md:text-base whitespace-pre-line">
                        {item.role}
                      </p>
                    </div>
                  </div>
                </article>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[var(--therme-mid)]/50 py-16 md:py-20">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <h2 className="therme-display text-2xl font-semibold md:text-3xl">Before you attend</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {THERME_NOTICES.map(({ icon: Icon, label, value }) => (
              <div key={label} className="therme-glass-card p-6">
                <Icon size={22} className="therme-notice-icon" aria-hidden />
                <p className="mt-4 text-[10px] font-medium uppercase tracking-[0.18em] text-[var(--therme-muted)]">
                  {label}
                </p>
                <p className="mt-2 text-lg font-medium text-[var(--therme-text)]">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ThermeGettingTherePremium />

      <footer className="border-t border-white/10 py-10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 md:px-8">
          <ThermeEventLogo event={e} className="text-[var(--therme-text)]" />
          <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-[var(--therme-muted)]">
            Powered by AivoryEMS
          </p>
        </div>
      </footer>
    </div>
  );
}
